// Public AI demo widget (Task 4.3 / D-37).
// SSR-safe: no window/document/localStorage at module or render scope; API call is client-only.
// Example chips use Phase 5 pre-warm phrases so cache hits are guaranteed once seeded.

import { useState, type FormEvent } from "react";
import { aiDemo, type AiDemoResult } from "../../lib/api.js";
import { AnchorButton, Button } from "../ui/Button.js";
import { useSignupMode } from "../../hooks/useSignupMode.js";
import { signupLabel } from "../../lib/signupCopy.js";

const EXAMPLE_CHIPS = [
  "beach day with kids",
  "camping trip",
  "moving house",
  "dinner party",
] as const;

const fieldCls =
  "w-full rounded-lg border border-line bg-white px-3 py-2.5 text-sm text-ink outline-none " +
  "placeholder:text-slate-400 focus-visible:border-brand-500 focus-visible:ring-2 focus-visible:ring-brand-500/20";

export function AiDemo() {
  const { mode: signupMode } = useSignupMode();
  const [purpose, setPurpose] = useState("");
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState<string | null>(null);
  const [result, setResult] = useState<AiDemoResult | null>(null);

  async function run(nextPurpose: string) {
    const trimmed = nextPurpose.trim();
    if (trimmed.length < 3) {
      setHint("Try a few more words — something like “weekend ski trip with toddlers.”");
      setResult(null);
      return;
    }
    setBusy(true);
    setHint(null);
    setResult(null);
    try {
      const res = await aiDemo(trimmed);
      setResult(res);
    } catch {
      // Never surface a hard error for a miss or network blip — invite signup instead.
      setResult({ cached: false });
    } finally {
      setBusy(false);
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    void run(purpose);
  }

  return (
    <div className="mt-8 rounded-2xl border border-line bg-white p-6 shadow-sm">
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label htmlFor="ai-demo-purpose" className="block text-sm font-medium text-ink">
            Describe a checklist
          </label>
          <div className="mt-1 flex flex-col gap-2 sm:flex-row">
            <input
              id="ai-demo-purpose"
              type="text"
              className={fieldCls}
              placeholder="e.g. weekend ski trip with toddlers"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              maxLength={120}
              autoComplete="off"
            />
            <Button type="submit" disabled={busy} className="shrink-0">
              {busy ? "Looking…" : "Try it"}
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {EXAMPLE_CHIPS.map((chip) => (
            <button
              key={chip}
              type="button"
              disabled={busy}
              onClick={() => {
                setPurpose(chip);
                void run(chip);
              }}
              className={
                "rounded-lg border border-line bg-[#f8fafc] px-3 py-1.5 text-sm text-ink " +
                "transition hover:border-brand-200 hover:bg-brand-50 " +
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 " +
                "disabled:cursor-not-allowed disabled:opacity-50"
              }
            >
              {chip}
            </button>
          ))}
        </div>
      </form>

      {hint ? <p className="mt-4 text-sm text-muted">{hint}</p> : null}

      {result?.cached && result.items ? (
        <div className="mt-6">
          {result.name ? (
            <h3 className="text-lg font-semibold text-ink">{result.name}</h3>
          ) : null}
          <ul className="mt-3 max-h-64 list-disc space-y-1 overflow-y-auto pl-5 text-sm text-ink">
            {result.items.map((item, i) => (
              <li key={`${i}-${item}`}>{item}</li>
            ))}
          </ul>
          <div className="mt-5">
            <AnchorButton href="/register">{signupLabel("save", signupMode)}</AnchorButton>
          </div>
        </div>
      ) : null}

      {result && !result.cached ? (
        <div className="mt-6 space-y-3">
          <p className="text-sm leading-relaxed text-muted">
            We haven&apos;t got that one cached — sign up and our AI will build it for you in
            seconds.
          </p>
          <AnchorButton href="/register">{signupLabel("generate", signupMode)}</AnchorButton>
        </div>
      ) : null}
    </div>
  );
}
